import type IProduct from "@api/models/IProduct";
import ButtonVariants from "@core-components/button/ButtonVariants";
import Emoji from "@core-components/emoji/Emoji";
import ProductDetailLine from "@core-components/product-detail-line/ProductDetailLine";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import type { AppRegion } from "@core-types/region.enum";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Group, Stack, Text } from "@mantine/core";
import AppSkeleton from "@modules/loader/AppSkeleton";
import CircleLoader from "@modules/loader/CircleLoader";
import { useLoaderStore } from "@modules/loader/useLoader.hook";
import { SkinProductRegionSelector } from "@modules/skins/SkinDetails/components/SkinProductRegionSelector";
import { IconBolt } from "@tabler/icons-react";

interface ProductDetailsMainProps {
  product?: IProduct;
  onBuy: () => void;
  region: AppRegion;
  handleRegionChange: (region: AppRegion) => void;
}

export const ProductDetailsMain = ({
  product,
  onBuy,
  handleRegionChange,
  region,
}: ProductDetailsMainProps) => {
  const { t } = useAppTranslation("components.productDetails");
  const productT = useAppTranslation();
  const loaderStore = useLoaderStore();

  const detailsLines = (
    product
      ? productT.t(`${product.i18nKey}.details_lines`, {
          returnObjects: true,
          region: region.toUpperCase(),
        })
      : []
  ) as string[];

  return (
    <Group align="stretch" className="w-full" gap={50} justify="center">
      <div
        className={
          "background-image-[var(--image-url)] relative hidden w-full max-w-[45%] flex-1 rounded-2xl bg-top bg-no-repeat px-[20px] pb-[16px] md:block"
        }
        style={
          {
            "--image-url": `url(${
              !loaderStore.isLoadingBySkeleton() && product?.thumbnail
            })`,
          } as React.CSSProperties
        }
      >
        <CircleLoader />
      </div>
      <Stack className="max-w-[100vw] flex-[0.5] px-3" gap={13}>
        <AppSkeleton>
          <SSHeadline props={{ title: { fz: 24 } }}>
            <Emoji.Fire />
            <SSWhiteText> {t("skeleton.title")} </SSWhiteText>
            <br />
            <SSLoLGradientText>
              {product &&
                productT.t(`${product.i18nKey}.tagline`, {
                  region: region.toUpperCase(),
                })}
            </SSLoLGradientText>
          </SSHeadline>
        </AppSkeleton>
        <div className="my-6 w-min space-y-4 ">
          <SkinProductRegionSelector
            region={region}
            onRegionChange={handleRegionChange}
            availableRegions={product?.availableIn}
          />
        </div>
        <Stack
          className="gradient-image-[var(--image-url)] relative rounded-2xl bg-primary-default px-3 py-3 pt-36 md:bg-none md:pt-3"
          gap={13}
          style={
            {
              "--image-url": `url(${
                !loaderStore.isLoadingBySkeleton() && product?.thumbnail
              })`,
            } as React.CSSProperties
          }
        >
          <CircleLoader />
          {detailsLines.map((d) => (
            <ProductDetailLine
              key={d}
              color="#00B67A"
              description={d}
              icon={IconBolt}
            />
          ))}
        </Stack>
        <div>
          <Text className="font-bold text-[23px] text-primary-white">
            {product?.price} €
          </Text>

          <ButtonVariants.BuyNow
            outOfStock={product?.accounts.length === 0}
            onClick={onBuy}
          />
        </div>
      </Stack>
    </Group>
  );
};
